import { memo } from 'react';
import { Icon } from 'semantic-ui-react';
import { useRowBoundingBox } from '../../../../../../stores/boundingBoxesStore';
import { ImportDescriptorIdType, ImportTableDescriptorType } from '../../../../../../types';
import { ImageHighlighterWrapper } from '../../../../../imageHighlighter/imageHighlighter';
import {
    useImportTableStoreDescriptorsEditMode,
    useImportTableStoreDescriptor,
    useImportTableStoreDescriptorActions,
    useImportTableStoreReportsFieldValue,
} from '../../../../context/importTableContext';
import { getDescriptorCellStyling } from './utils';
import { HIGHLIGHTER_VARIANT, IMPORT_DESCRIPTOR_SOURCE } from '../../../../../../constants/generalConstants';
import ObjCheck from '../../../../../../../../../utils/objCheck';

type DescriptorLabelCellPropsType = {
    descriptorId: ImportDescriptorIdType;
    openDescriptorSettings: (descriptor: { calculation: string; type: string; id: string; label: string }) => void;
};
const DescriptorLabelCell = memo(function DescriptorCell({
    descriptorId,
    openDescriptorSettings,
}: DescriptorLabelCellPropsType) {
    const descriptor = useImportTableStoreDescriptor(descriptorId);
    const editing = useImportTableStoreDescriptorsEditMode();
    const { updateDescriptorLabel, enableDescriptorsEditMode } = useImportTableStoreDescriptorActions();
    const rowBoundingBox = useRowBoundingBox(descriptor.id);
    const fieldValues = useImportTableStoreReportsFieldValue(descriptorId);
    const importValues = fieldValues.filter(
        (fieldValue) => !ObjCheck.isNullUndefinedOrEmpty(fieldValue.normalizedValue)
    );
    const noImportValues = !importValues.length
        ? false
        : importValues.every((fieldValue) => fieldValue.normalizedValueMatchesMaster);
    const { styles, classes } = getDescriptorCellStyling(descriptor, editing);

    return (
        <td style={styles} className={classes} onDoubleClick={enableDescriptorsEditMode}>
            <ImageHighlighterWrapper coordinates={[{ ...rowBoundingBox, variant: HIGHLIGHTER_VARIANT.PRIMARY }]}>
                {editing ? (
                    <EditDescriptorLabel
                        openSettings={() =>
                            openDescriptorSettings({
                                calculation: descriptor.verticalCalculation,
                                id: descriptor.id,
                                type: descriptor.dataType.toLowerCase(),
                                label: descriptor.label,
                            })
                        }
                        descriptor={descriptor}
                        handleChange={(data) => updateDescriptorLabel(descriptor.id, data)}
                        disabled={descriptor.source === IMPORT_DESCRIPTOR_SOURCE.MASTER_TABLE || noImportValues}
                    />
                ) : (
                    <ReadDescriptorLabel descriptor={descriptor} />
                )}
            </ImageHighlighterWrapper>
        </td>
    );
});

export default DescriptorLabelCell;

type EditDescriptorLabelPropsType = {
    descriptor: ImportTableDescriptorType;
    handleChange: (data: string) => void;
    disabled?: boolean;
    openSettings: () => void;
};
function EditDescriptorLabel({ descriptor, handleChange, disabled, openSettings }: EditDescriptorLabelPropsType) {
    return (
        <div className="flex full-height flex-align-center">
            <input
                disabled={disabled}
                className="cell-input outline--none border-w--1"
                style={{ width: 400 }}
                type="text"
                value={descriptor.label}
                onChange={(e) => handleChange(e.target.value)}
            />
            <Icon
                onClick={openSettings}
                name="ellipsis vertical"
                className="ml-auto flex flex-align-center flex-justify-center cursorPointer"
            />
        </div>
    );
}

type ReadDescriptorLabelPropsType = {
    descriptor: ImportTableDescriptorType;
};
function ReadDescriptorLabel({ descriptor }: ReadDescriptorLabelPropsType) {
    return (
        <div className="flex flex-align-center flex-gap-1">
            <div className="flex flex-align-center">{descriptor.label}</div>
            {descriptor.newDescriptor && (
                <Icon className="flex flex-align-center flex-justify-center" color="yellow" name="plus" />
            )}
        </div>
    );
}
